import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { Box, Stack, Typography } from "@mui/material";

const AXIS = {
  grid: "#eef2f7",
  domain: "#e2e8f0",
  label: "#64748b",
  tooltipBg: "#0f172a",
};

const formatL = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0.0L";
  return `${(n / 100000).toFixed(1)}L`;
};
export const formatPct = (v) => `${Number(v || 0).toFixed(1)}%`;
const formatCurrency = (v) => `Rs. ${Number(v || 0).toLocaleString("en-IN")}`;

const useContainerSize = (minH = 220) => {
  const ref = useRef(null);
  const [size, setSize] = useState({ width: 0, height: minH });

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let frame = 0;
    const measure = () => {
      const w = Math.round(el.getBoundingClientRect().width || 0);
      setSize((prev) => {
        if (Math.abs(prev.width - w) < 2 && prev.height === minH) return prev;
        return { width: w, height: minH };
      });
    };
    const onResize = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    measure();
    const ro = new ResizeObserver(onResize);
    ro.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      ro.disconnect();
    };
  }, [minH]);

  return [ref, size];
};

let hideTipTimer = null;

const ensureTipStyle = () => {
  if (document.getElementById("sbt-d3-tip-style")) return;
  const style = document.createElement("style");
  style.id = "sbt-d3-tip-style";
  style.textContent = `
    .sbt-d3-tip {
      position: fixed;
      z-index: 10000;
      max-width: 360px;
      max-height: min(240px, calc(100vh - 24px));
      overflow-y: auto;
      overflow-x: hidden;
      overscroll-behavior: contain;
      pointer-events: auto;
      background: ${AXIS.tooltipBg};
      color: #fff;
      padding: 8px 12px;
      border-radius: 8px;
      font-size: 12px;
      line-height: 1.45;
      box-shadow: 0 8px 24px rgba(15,23,42,0.28);
      scrollbar-width: thin;
      scrollbar-color: rgba(255,255,255,0.45) transparent;
    }
    .sbt-d3-tip::-webkit-scrollbar { width: 8px; }
    .sbt-d3-tip::-webkit-scrollbar-track { background: transparent; }
    .sbt-d3-tip::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.4); border-radius: 8px; }
  `;
  document.head.appendChild(style);
};

const ensureTooltip = () => {
  ensureTipStyle();
  let tip = d3.select("body").select(".sbt-d3-tip");
  if (!tip.empty() && tip.attr("data-scroll") !== "1") {
    tip.remove();
    tip = d3.select("body").select(".sbt-d3-tip");
  }
  if (tip.empty()) {
    tip = d3.select("body").append("div").attr("class", "sbt-d3-tip").attr("data-scroll", "1");
    tip
      .on("mouseenter", () => {
        if (hideTipTimer) clearTimeout(hideTipTimer);
      })
      .on("mouseleave", hideTip)
      .on("wheel", (event) => {
        event.stopPropagation();
      });
  }
  return tip;
};

const positionTip = (tip, event) => {
  if (!event || event.target?.closest?.(".sbt-d3-tip")) return;
  const node = tip.node();
  if (!node) return;
  const pad = 12;
  const tw = node.offsetWidth || 160;
  const th = node.offsetHeight || 50;
  const spaceAbove = event.clientY - pad;
  const spaceBelow = window.innerHeight - event.clientY - pad;
  const placeAbove = spaceAbove >= spaceBelow;

  let left = event.clientX + 14;
  if (left + tw > window.innerWidth - pad) left = event.clientX - tw - 14;
  left = Math.max(pad, Math.min(left, window.innerWidth - tw - pad));

  let top = placeAbove ? event.clientY - th - 10 : event.clientY + 14;
  top = Math.max(pad, Math.min(top, window.innerHeight - th - pad));
  tip.style("left", `${left}px`).style("top", `${top}px`);
};

const showTip = (html, event) => {
  if (hideTipTimer) clearTimeout(hideTipTimer);
  const tip = ensureTooltip();
  const node = tip.node();
  const prevHtml = node?.getAttribute("data-html");
  if (prevHtml !== html) {
    tip.html(html).attr("data-html", html);
    if (node) node.scrollTop = 0;
  }
  tip.style("opacity", "1").style("pointer-events", "auto");
  positionTip(tip, event);
};

const moveTip = (event) => {
  const tip = d3.select("body").select(".sbt-d3-tip");
  if (tip.empty() || tip.style("opacity") === "0") return;
  positionTip(tip, event);
};

const hideTip = () => {
  if (hideTipTimer) clearTimeout(hideTipTimer);
  d3.select("body").select(".sbt-d3-tip").style("opacity", "0").style("pointer-events", "none");
};

const hideTipSoon = () => {
  if (hideTipTimer) clearTimeout(hideTipTimer);
  hideTipTimer = setTimeout(hideTip, 220);
};

const Legend = ({ items = [] }) => {
  if (!items.length) return null;
  return (
    <Stack direction="row" flexWrap="wrap" useFlexGap justifyContent="center" sx={{ mt: 1, gap: "8px 14px" }}>
      {items.map((item) => (
        <Stack key={item.label} direction="row" alignItems="center" spacing={0.75}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: item.color, flexShrink: 0 }} />
          <Typography sx={{ fontSize: 11, color: AXIS.label, fontWeight: 500 }}>{item.label}</Typography>
        </Stack>
      ))}
    </Stack>
  );
};

const styleYAxis = (g) => {
  g.select(".domain").remove();
  g.selectAll("line").remove();
  g.selectAll("text").style("font-size", "10px").style("fill", AXIS.label).style("font-family", "inherit");
};

const styleXAxis = (g, rotate = false) => {
  g.select(".domain").attr("stroke", AXIS.domain);
  g.selectAll("line").attr("stroke", AXIS.domain);
  g.selectAll("text")
    .style("font-size", "10px")
    .style("fill", AXIS.label)
    .style("font-family", "inherit");
  if (rotate) {
    g.selectAll("text").attr("transform", "rotate(-32)").style("text-anchor", "end");
  }
};

const shortPoLabel = (po) => {
  const parts = String(po || "").split("/").filter(Boolean);
  if (parts.length >= 3) return parts.slice(2, 4).join("/");
  return String(po || "").slice(-14);
};

export const D3DonutChart = ({ data = [], height = 240, centerLabel = "Total" }) => {
  const [wrapRef, size] = useContainerSize(height);
  const svgRef = useRef(null);
  const rows = (data || []).filter((d) => Number.isFinite(Number(d.value)) && Number(d.value) > 0);
  const rowsKey = JSON.stringify(rows.map((d) => [d.id, d.label, d.value, d.color]));

  useEffect(() => {
    const width = size.width;
    const chartRows = (data || []).filter((d) => Number.isFinite(Number(d.value)) && Number(d.value) > 0);
    const chartTotal = chartRows.reduce((sum, d) => sum + Number(d.value || 0), 0);
    if (!svgRef.current || width < 40 || !chartRows.length) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    svg.attr("width", width).attr("height", height).attr("viewBox", `0 0 ${width} ${height}`);

    const radius = Math.min(width, height) / 2 - 8;
    const g = svg.append("g").attr("transform", `translate(${width / 2},${height / 2})`);
    const pie = d3.pie().sort(null).value((d) => Number(d.value) || 0).padAngle(0.02);
    const arc = d3.arc().innerRadius(radius * 0.58).outerRadius(radius * 0.92).cornerRadius(4);
    const hoverArc = d3.arc().innerRadius(radius * 0.56).outerRadius(radius * 0.98).cornerRadius(4);

    g.selectAll("path")
      .data(pie(chartRows))
      .join("path")
      .attr("fill", (d) => d.data.color)
      .attr("stroke", "#fff")
      .attr("stroke-width", 2)
      .style("cursor", "pointer")
      .attr("d", arc)
      .on("mouseenter", function (event, d) {
        d3.select(this).transition().duration(160).attr("d", hoverArc);
        const pct = chartTotal ? ((d.data.value / chartTotal) * 100).toFixed(1) : "0.0";
        showTip(
          `<div style="font-weight:600;margin-bottom:2px">${d.data.label}</div>${formatCurrency(d.data.value)}<div style="opacity:.75">${pct}%</div>`,
          event
        );
      })
      .on("mousemove", moveTip)
      .on("mouseleave", function () {
        d3.select(this).transition().duration(160).attr("d", arc);
        hideTipSoon();
      });

    g.append("text")
      .attr("text-anchor", "middle")
      .attr("dy", "-0.2em")
      .style("font-size", "11px")
      .style("fill", AXIS.label)
      .style("font-weight", "600")
      .text(centerLabel);

    g.append("text")
      .attr("text-anchor", "middle")
      .attr("dy", "1.15em")
      .style("font-size", "13px")
      .style("fill", "#0f172a")
      .style("font-weight", "700")
      .text(formatL(chartTotal));

    return () => hideTip();
  }, [size.width, height, rowsKey, centerLabel]);

  return (
    <Box>
      <Box ref={wrapRef} sx={{ width: "100%", height }}>
        <svg ref={svgRef} style={{ width: "100%", height: "100%", display: "block" }} />
      </Box>
      <Legend items={rows.map((d) => ({ label: d.label, color: d.color }))} />
    </Box>
  );
};

export const D3BudgetVsActual = ({ budget = 0, grn = 0, pending = 0, cancelled = 0, height = 240 }) => {
  const [wrapRef, size] = useContainerSize(height);
  const svgRef = useRef(null);
  const series = [
    { key: "budget", label: "Budget", color: "#3b82f6" },
    { key: "grn", label: "GRN Received", color: "#10b981" },
    { key: "pending", label: "Pending", color: "#f59e0b" },
    { key: "cancelled", label: "Cancelled", color: "#ef4444" },
  ];

  useEffect(() => {
    const width = size.width;
    if (!svgRef.current || width < 40) return;

    const margin = { top: 12, right: 12, bottom: 36, left: 48 };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    svg.attr("width", width).attr("height", height).attr("viewBox", `0 0 ${width} ${height}`);
    const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

    const categories = ["Budget", "Actual"];
    const actualTotal = Number(grn) + Number(pending) + Number(cancelled);
    const maxY = Math.max(Number(budget) || 0, actualTotal, 1) * 1.12;
    const x = d3.scaleBand().domain(categories).range([0, innerW]).padding(0.38);
    const y = d3.scaleLinear().domain([0, maxY]).nice().range([innerH, 0]);

    g.append("g")
      .call(d3.axisLeft(y).ticks(4).tickSize(-innerW).tickFormat(""))
      .call((ax) => {
        ax.select(".domain").remove();
        ax.selectAll("line").attr("stroke", AXIS.grid);
      });
    g.append("g").call(d3.axisLeft(y).ticks(4).tickFormat(formatL)).call(styleYAxis);
    g.append("g").attr("transform", `translate(0,${innerH})`).call(d3.axisBottom(x).tickSize(0)).call(styleXAxis);

    const barW = x.bandwidth();
    const budgetX = x("Budget");
    g.append("rect")
      .attr("x", budgetX)
      .attr("y", innerH)
      .attr("width", barW)
      .attr("height", 0)
      .attr("rx", 6)
      .attr("fill", "#3b82f6")
      .style("cursor", "pointer")
      .on("mouseenter", (event) => showTip(`<div style="font-weight:600">Budget</div>${formatCurrency(budget)}`, event))
      .on("mousemove", moveTip)
      .on("mouseleave", hideTipSoon)
      .transition()
      .duration(500)
      .attr("y", y(Number(budget) || 0))
      .attr("height", innerH - y(Number(budget) || 0));

    const stackRows = [{ grn: Number(grn) || 0, pending: Number(pending) || 0, cancelled: Number(cancelled) || 0 }];
    const stacked = d3.stack().keys(["grn", "pending", "cancelled"])(stackRows);
    const colorByKey = { grn: "#10b981", pending: "#f59e0b", cancelled: "#ef4444" };
    const labelByKey = { grn: "GRN Received", pending: "Pending", cancelled: "Cancelled" };
    const actualX = x("Actual");

    stacked.forEach((layer) => {
      const [y0, y1] = layer[0];
      const h = y(y0) - y(y1);
      if (h <= 0) return;
      g.append("rect")
        .attr("x", actualX)
        .attr("y", innerH)
        .attr("width", barW)
        .attr("height", 0)
        .attr("rx", 4)
        .attr("fill", colorByKey[layer.key])
        .style("cursor", "pointer")
        .on("mouseenter", (event) =>
          showTip(`<div style="font-weight:600">${labelByKey[layer.key]}</div>${formatCurrency(y1 - y0)}`, event)
        )
        .on("mousemove", moveTip)
        .on("mouseleave", hideTipSoon)
        .transition()
        .duration(500)
        .attr("y", y(y1))
        .attr("height", h);
    });

    if (actualTotal > Number(budget) && Number(budget) > 0) {
      g.append("line")
        .attr("x1", 0)
        .attr("x2", innerW)
        .attr("y1", y(Number(budget)))
        .attr("y2", y(Number(budget)))
        .attr("stroke", "#dc2626")
        .attr("stroke-dasharray", "4,3")
        .attr("stroke-width", 1.5);
      g.append("text")
        .attr("x", innerW)
        .attr("y", y(Number(budget)) - 6)
        .attr("text-anchor", "end")
        .style("font-size", "10px")
        .style("font-weight", "600")
        .style("fill", "#dc2626")
        .text("Budget limit");
    }

    return () => hideTip();
  }, [size.width, height, budget, grn, pending, cancelled]);

  return (
    <Box>
      <Box ref={wrapRef} sx={{ width: "100%", height }}>
        <svg ref={svgRef} style={{ width: "100%", height: "100%", display: "block" }} />
      </Box>
      <Legend items={series} />
    </Box>
  );
};

const GROUPED_BAR_SERIES = [
  { key: "po", label: "PO Amt (w tax)", color: "#6366f1" },
  { key: "canceled", label: "Cancelled", color: "#9ca3af" },
  { key: "completed", label: "GRN Received", color: "#10b981" },
  { key: "pending", label: "Pending", color: "#f59e0b" },
];

export const D3GroupedBarChart = ({ rows = [], height = 280 }) => {
  const [wrapRef, size] = useContainerSize(height);
  const svgRef = useRef(null);
  const series = GROUPED_BAR_SERIES;
  const rowsKey = JSON.stringify(
    (rows || []).map((row) => [row.po_number, row.po, row.canceled, row.completed, row.pending])
  );

  useEffect(() => {
    const width = size.width;
    const chartRows = rows || [];
    if (!svgRef.current || width < 40 || !chartRows.length) return;

    const margin = { top: 12, right: 8, bottom: 52, left: 48 };
    const innerW = Math.max(width - margin.left - margin.right, 40);
    const innerH = height - margin.top - margin.bottom;
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    svg.attr("width", width).attr("height", height).attr("viewBox", `0 0 ${width} ${height}`);
    const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

    const labels = chartRows.map((row, i) => {
      const base = shortPoLabel(row.po_number) || `PO ${i + 1}`;
      return `${base}`;
    });
    const uniqueLabels = labels.map((label, i) => (labels.indexOf(label) === i ? label : `${label} (${i + 1})`));
    const maxY = Math.max(
      d3.max(chartRows, (row) => Math.max(Number(row.po) || 0, Number(row.canceled) || 0, Number(row.completed) || 0, Number(row.pending) || 0)) || 0,
      1
    ) * 1.12;
    const x0 = d3.scaleBand().domain(uniqueLabels).range([0, innerW]).padding(0.22);
    const x1 = d3.scaleBand().domain(series.map((s) => s.key)).range([0, Math.max(x0.bandwidth(), 1)]).padding(0.12);
    const y = d3.scaleLinear().domain([0, Number.isFinite(maxY) ? maxY : 1]).nice().range([innerH, 0]);

    g.append("g")
      .call(d3.axisLeft(y).ticks(4).tickSize(-innerW).tickFormat(""))
      .call((ax) => {
        ax.select(".domain").remove();
        ax.selectAll("line").attr("stroke", AXIS.grid);
      });
    g.append("g").call(d3.axisLeft(y).ticks(4).tickFormat(formatL)).call(styleYAxis);
    g.append("g")
      .attr("transform", `translate(0,${innerH})`)
      .call(d3.axisBottom(x0).tickSize(0))
      .call((ax) => styleXAxis(ax, uniqueLabels.length > 5));

    chartRows.forEach((row, i) => {
      const groupX = x0(uniqueLabels[i]);
      if (groupX == null) return;
      const group = g.append("g").attr("transform", `translate(${groupX},0)`);
      series.forEach((s) => {
        const value = Number(row[s.key]) || 0;
        group
          .append("rect")
          .attr("x", x1(s.key))
          .attr("y", innerH)
          .attr("width", x1.bandwidth())
          .attr("height", 0)
          .attr("rx", 3)
          .attr("fill", s.color)
          .style("cursor", "pointer")
          .on("mouseenter", (event) =>
            showTip(
              `<div style="font-weight:600;margin-bottom:2px">${row.po_number}</div>${s.label}<br/>${formatCurrency(value)}`,
              event
            )
          )
          .on("mousemove", moveTip)
          .on("mouseleave", hideTipSoon)
          .transition()
          .duration(450)
          .attr("y", y(value))
          .attr("height", innerH - y(value));
      });
    });

    return () => hideTip();
  }, [size.width, height, rowsKey]);

  return (
    <Box>
      <Box ref={wrapRef} sx={{ width: "100%", height }}>
        <svg ref={svgRef} style={{ width: "100%", height: "100%", display: "block" }} />
      </Box>
      <Legend items={series} />
    </Box>
  );
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const tooltipDetailsHtml = (row) => {
  if (Array.isArray(row.details) && row.details.length) {
    return row.details
      .map(
        (item) =>
          `<div style="margin-top:6px;padding-top:6px;border-top:1px solid rgba(255,255,255,.14)">
            <div style="font-weight:600">${escapeHtml(item.title)}</div>
            ${(item.lines || [])
              .filter(Boolean)
              .map((line) => `<div style="opacity:.8">${escapeHtml(line)}</div>`)
              .join("")}
          </div>`
      )
      .join("");
  }
  return "";
};

export const D3HorizontalBarChart = ({
  rows = [],
  height = 280,
  color = "#f59e0b",
  valueFormatter = formatCurrency,
  yTickMax = 16,
  domainMax,
}) => {
  const [wrapRef, size] = useContainerSize(height);
  const svgRef = useRef(null);
  const rowsKey = JSON.stringify((rows || []).map((row) => [row.label, row.value, row.color, row.meta, row.details]));

  useEffect(() => {
    const width = size.width;
    const chartRows = rows || [];
    if (!svgRef.current || width < 40 || !chartRows.length) return;

    const longest = d3.max(chartRows, (d) => String(d.label || "").length) || 8;
    const left = Math.min(Math.max(longest * 6.2, 72), 140);
    const margin = { top: 8, right: 16, bottom: 28, left };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    svg.attr("width", width).attr("height", height).attr("viewBox", `0 0 ${width} ${height}`);
    const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

    const y = d3.scaleBand().domain(chartRows.map((d) => d.label)).range([0, innerH]).padding(0.28);
    const dataMax = d3.max(chartRows, (d) => Number(d.value) || 0) || 0;
    const cap = domainMax == null ? null : Number(domainMax);
    const maxX = cap != null ? Math.max(cap, dataMax, 1) : Math.max(dataMax, 1) * 1.08;
    const xScale = d3.scaleLinear().domain([0, maxX]).range([0, innerW]);
    if (cap == null) xScale.nice();
    const x = xScale;
    const tickValues =
      cap != null && cap > 0
        ? maxX <= cap * 1.02
          ? [0, cap * 0.25, cap * 0.5, cap * 0.75, cap]
          : Array.from(new Set([0, cap * 0.5, cap, maxX])).sort((a, b) => a - b)
        : null;

    g.append("g")
      .attr("transform", `translate(0,${innerH})`)
      .call(
        d3
          .axisBottom(x)
          .ticks(4)
          .tickSize(-innerH)
          .tickValues(tickValues || null)
          .tickFormat("")
      )
      .call((ax) => {
        ax.select(".domain").remove();
        ax.selectAll("line").attr("stroke", AXIS.grid);
      });
    g.append("g")
      .attr("transform", `translate(0,${innerH})`)
      .call(
        d3
          .axisBottom(x)
          .ticks(4)
          .tickValues(tickValues || null)
          .tickFormat((d) => valueFormatter(d))
          .tickSize(0)
      )
      .call(styleXAxis);
    if (cap != null && maxX > cap) {
      g.append("line")
        .attr("x1", x(cap))
        .attr("x2", x(cap))
        .attr("y1", 0)
        .attr("y2", innerH)
        .attr("stroke", "#dc2626")
        .attr("stroke-dasharray", "4,3")
        .attr("stroke-width", 1.25);
    }
    g.append("g")
      .call(d3.axisLeft(y).tickSize(0))
      .call((ax) => {
        ax.select(".domain").remove();
        ax.selectAll("text")
          .style("font-size", "10px")
          .style("fill", AXIS.label)
          .text((d) => (String(d).length > yTickMax ? `${String(d).slice(0, yTickMax - 1)}…` : d));
      });

    g.selectAll("rect.bar")
      .data(chartRows)
      .join("rect")
      .attr("class", "bar")
      .attr("x", 0)
      .attr("y", (d) => y(d.label))
      .attr("height", y.bandwidth())
      .attr("width", 0)
      .attr("rx", 5)
      .attr("fill", (d) => d.color || color)
      .style("cursor", "pointer")
      .on("mouseenter", (event, d) =>
        showTip(
          `<div style="font-weight:600">${escapeHtml(d.label)}</div>${escapeHtml(valueFormatter(d.value))}${d.meta ? `<div style="opacity:.75">${escapeHtml(d.meta)}</div>` : ""}${tooltipDetailsHtml(d)}`,
          event
        )
      )
      .on("mousemove", moveTip)
      .on("mouseleave", hideTipSoon)
      .transition()
      .duration(500)
      .attr("width", (d) => Math.max(x(Number(d.value) || 0), 2));

    return () => hideTip();
  }, [size.width, height, rowsKey, color, valueFormatter, yTickMax, domainMax]);

  return (
    <Box ref={wrapRef} sx={{ width: "100%", height }}>
      <svg ref={svgRef} style={{ width: "100%", height: "100%", display: "block" }} />
    </Box>
  );
};

const VENDOR_STACK_SERIES = [
  { key: "grn", label: "GRN Received", color: "#10b981" },
  { key: "pending", label: "Pending", color: "#f59e0b" },
  { key: "cancelled", label: "Cancelled", color: "#9ca3af" },
];

export const D3StackedHorizontalBarChart = ({
  rows = [],
  series = VENDOR_STACK_SERIES,
  height = 260,
  yTickMax = 16,
  onRowClick,
}) => {
  const [wrapRef, size] = useContainerSize(height);
  const svgRef = useRef(null);
  const onRowClickRef = useRef(onRowClick);
  onRowClickRef.current = onRowClick;
  const rowsKey = JSON.stringify((rows || []).map((row) => [row.label, row.grn, row.pending, row.cancelled]));
  const seriesKey = JSON.stringify(series.map((item) => [item.key, item.label, item.color]));

  useEffect(() => {
    const width = size.width;
    const chartRows = rows || [];
    if (!svgRef.current || width < 40 || !chartRows.length || !series.length) return;

    const longest = d3.max(chartRows, (d) => String(d.label || "").length) || 8;
    const left = Math.min(Math.max(longest * 6.2, 72), 140);
    const margin = { top: 8, right: 16, bottom: 28, left };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    svg.attr("width", width).attr("height", height).attr("viewBox", `0 0 ${width} ${height}`);
    const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

    const keys = series.map((item) => item.key);
    const stacked = d3.stack().keys(keys)(chartRows);
    const y = d3.scaleBand().domain(chartRows.map((d) => d.label)).range([0, innerH]).padding(0.28);
    const maxX = Math.max(d3.max(chartRows, (d) => keys.reduce((sum, key) => sum + (Number(d[key]) || 0), 0)) || 0, 1) * 1.08;
    const x = d3.scaleLinear().domain([0, maxX]).nice().range([0, innerW]);
    const color = d3.scaleOrdinal().domain(keys).range(series.map((item) => item.color));

    g.append("g")
      .attr("transform", `translate(0,${innerH})`)
      .call(d3.axisBottom(x).ticks(4).tickSize(-innerH).tickFormat(""))
      .call((ax) => {
        ax.select(".domain").remove();
        ax.selectAll("line").attr("stroke", AXIS.grid);
      });
    g.append("g")
      .attr("transform", `translate(0,${innerH})`)
      .call(d3.axisBottom(x).ticks(4).tickFormat(formatL).tickSize(0))
      .call(styleXAxis);
    g.append("g")
      .call(d3.axisLeft(y).tickSize(0))
      .call((ax) => {
        ax.select(".domain").remove();
        ax.selectAll("text")
          .style("font-size", "10px")
          .style("fill", AXIS.label)
          .style("cursor", "pointer")
          .text((d) => (String(d).length > yTickMax ? `${String(d).slice(0, yTickMax - 1)}…` : d))
          .on("click", (event, d) => {
            event.stopPropagation();
            onRowClickRef.current?.({ label: d });
          });
      });

    stacked.forEach((layer) => {
      const key = layer.key;
      const seriesItem = series.find((item) => item.key === key);
      g.selectAll(`rect.stack-${key}`)
        .data(layer)
        .join("rect")
        .attr("class", `stack-${key}`)
        .attr("x", (d) => x(d[0]))
        .attr("y", (d) => y(d.data.label))
        .attr("height", y.bandwidth())
        .attr("width", 0)
        .attr("fill", color(key))
        .style("cursor", "pointer")
        .on("click", (event, d) => {
          event.stopPropagation();
          onRowClickRef.current?.({ label: d.data.label, ...d.data });
        })
        .on("mouseenter", (event, d) => {
          const value = (Number(d[1]) || 0) - (Number(d[0]) || 0);
          const total = keys.reduce((sum, k) => sum + (Number(d.data[k]) || 0), 0);
          showTip(
            `<div style="font-weight:600;margin-bottom:2px">${d.data.label}</div>${seriesItem?.label || key}: ${formatCurrency(value)}<div style="opacity:.75">Total: ${formatCurrency(total)}</div>`,
            event
          );
        })
        .on("mousemove", moveTip)
        .on("mouseleave", hideTipSoon)
        .transition()
        .duration(500)
        .attr("width", (d) => Math.max(x(d[1]) - x(d[0]), 0));

      g.selectAll(`text.stack-label-${key}`)
        .data(layer)
        .join("text")
        .attr("class", `stack-label-${key}`)
        .attr("x", (d) => (x(d[0]) + x(d[1])) / 2)
        .attr("y", (d) => (y(d.data.label) || 0) + y.bandwidth() / 2)
        .attr("dy", "0.35em")
        .attr("text-anchor", "middle")
        .style("font-size", "10px")
        .style("font-weight", "700")
        .style("fill", "#fff")
        .style("pointer-events", "none")
        .style("opacity", (d) => {
          const value = (Number(d[1]) || 0) - (Number(d[0]) || 0);
          const segW = x(d[1]) - x(d[0]);
          return value > 0 && segW >= 36 ? 1 : 0;
        })
        .text((d) => formatL((Number(d[1]) || 0) - (Number(d[0]) || 0)));
    });

    return () => hideTip();
  }, [size.width, height, rowsKey, seriesKey, yTickMax]);

  return (
    <Box>
      <Box ref={wrapRef} sx={{ width: "100%", height }}>
        <svg ref={svgRef} style={{ width: "100%", height: "100%", display: "block" }} />
      </Box>
      <Legend items={series} />
    </Box>
  );
};

export const D3SimpleBarChart = ({ rows = [], height = 240, valueFormatter = formatCurrency }) => {
  const [wrapRef, size] = useContainerSize(height);
  const svgRef = useRef(null);
  const formatterRef = useRef(valueFormatter);
  formatterRef.current = valueFormatter;
  const rowsKey = JSON.stringify((rows || []).map((row) => [row.label, row.value, row.color]));

  useEffect(() => {
    const width = size.width;
    const chartRows = rows || [];
    const formatValue = formatterRef.current || formatCurrency;
    if (!svgRef.current || width < 40 || !chartRows.length) return;

    const margin = { top: 16, right: 12, bottom: 44, left: 56 };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    svg.attr("width", width).attr("height", height).attr("viewBox", `0 0 ${width} ${height}`);
    const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

    const x = d3.scaleBand().domain(chartRows.map((d) => d.label)).range([0, innerW]).padding(0.35);
    const maxY = Math.max(d3.max(chartRows, (d) => Number(d.value) || 0) || 0, 1) * 1.12;
    const y = d3.scaleLinear().domain([0, maxY]).nice().range([innerH, 0]);

    g.append("g")
      .call(d3.axisLeft(y).ticks(4).tickSize(-innerW).tickFormat(""))
      .call((ax) => {
        ax.select(".domain").remove();
        ax.selectAll("line").attr("stroke", AXIS.grid);
      });
    g.append("g").call(d3.axisLeft(y).ticks(4).tickFormat((d) => formatValue(d))).call(styleYAxis);
    g.append("g").attr("transform", `translate(0,${innerH})`).call(d3.axisBottom(x).tickSize(0)).call(styleXAxis);

    g.selectAll("rect.bar")
      .data(chartRows)
      .join("rect")
      .attr("x", (d) => x(d.label))
      .attr("width", x.bandwidth())
      .attr("height", 0)
      .attr("y", innerH)
      .attr("rx", 6)
      .attr("fill", (d) => d.color || "#3b82f6")
      .style("cursor", "pointer")
      .on("mouseenter", (event, d) =>
        showTip(`<div style="font-weight:600">${d.label}</div>${formatValue(d.value)}`, event)
      )
      .on("mousemove", moveTip)
      .on("mouseleave", hideTipSoon)
      .transition()
      .duration(500)
      .attr("y", (d) => y(Number(d.value) || 0))
      .attr("height", (d) => innerH - y(Number(d.value) || 0));

    return () => hideTip();
  }, [size.width, height, rowsKey]);

  return (
    <Box ref={wrapRef} sx={{ width: "100%", height }}>
      <svg ref={svgRef} style={{ width: "100%", height: "100%", display: "block" }} />
    </Box>
  );
};

export const D3MultiLineChart = ({ labels = [], series = [], height = 260 }) => {
  const [wrapRef, size] = useContainerSize(height);
  const svgRef = useRef(null);
  const dataKey = JSON.stringify({
    labels,
    series: (series || []).map((s) => [s.key, s.label, s.color, s.values, s.metas, s.details]),
  });

  useEffect(() => {
    const width = size.width;
    if (!svgRef.current || width < 40 || !labels.length || !series.length) return;

    const margin = { top: 12, right: 12, bottom: 40, left: 48 };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    svg.attr("width", width).attr("height", height).attr("viewBox", `0 0 ${width} ${height}`);
    const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

    const maxY = Math.max(d3.max(series, (s) => d3.max(s.values, (v) => Number(v) || 0)) || 0, 1) * 1.08;
    const x = d3.scalePoint().domain(labels).range([0, innerW]).padding(0.2);
    const y = d3.scaleLinear().domain([0, maxY]).nice().range([innerH, 0]);

    g.append("g")
      .call(d3.axisLeft(y).ticks(4).tickSize(-innerW).tickFormat(""))
      .call((ax) => {
        ax.select(".domain").remove();
        ax.selectAll("line").attr("stroke", AXIS.grid);
      });
    g.append("g").call(d3.axisLeft(y).ticks(4).tickFormat(formatL)).call(styleYAxis);
    g.append("g")
      .attr("transform", `translate(0,${innerH})`)
      .call(d3.axisBottom(x).tickSize(0))
      .call((ax) => styleXAxis(ax, labels.length > 8));

    series.forEach((s) => {
      const points = labels.map((label, i) => ({
        label,
        value: Number(s.values[i]) || 0,
        meta: s.metas?.[i],
        details: s.details?.[i] || [],
      }));
      const line = d3
        .line()
        .x((d) => x(d.label))
        .y((d) => y(d.value))
        .curve(d3.curveMonotoneX);
      g.append("path")
        .datum(points)
        .attr("fill", "none")
        .attr("stroke", s.color)
        .attr("stroke-width", 2.4)
        .attr("stroke-linecap", "round")
        .attr("d", line);
      g.selectAll(`circle.${s.key || s.label}`)
        .data(points)
        .join("circle")
        .attr("cx", (d) => x(d.label))
        .attr("cy", (d) => y(d.value))
        .attr("r", 3.5)
        .attr("fill", s.color)
        .attr("stroke", "#fff")
        .attr("stroke-width", 1.5)
        .style("cursor", "pointer")
        .on("mouseenter", (event, d) =>
          showTip(
            `<div style="font-weight:600">${escapeHtml(d.label)}</div>${escapeHtml(s.label)}<br/>${escapeHtml(formatCurrency(d.value))}${d.meta ? `<div style="opacity:.75">${escapeHtml(d.meta)}</div>` : ""}${tooltipDetailsHtml(d)}`,
            event
          )
        )
        .on("mousemove", moveTip)
        .on("mouseleave", hideTipSoon);
    });

    return () => hideTip();
  }, [size.width, height, dataKey]);

  return (
    <Box>
      <Box ref={wrapRef} sx={{ width: "100%", height }}>
        <svg ref={svgRef} style={{ width: "100%", height: "100%", display: "block" }} />
      </Box>
      <Legend items={series.map((s) => ({ label: s.label, color: s.color }))} />
    </Box>
  );
};
